import { useState, useEffect } from 'react';
import { AlertTriangle, Receipt } from 'lucide-react';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { useI18n } from '@/hooks/useI18n';
import { IInstallment } from '@/lib/builder/models/payment-plan.model';
import {
    MANUAL_PAYMENT_METHODS,
    PLATFORM_FEE_RATE,
    platformFeeOn,
    type ManualPaymentMethod,
} from '@/lib/payments/utils/payment-method';

interface MarkInstallmentPaidModalProps {
    installment: IInstallment | null;
    isOpen: boolean;
    isSaving: boolean;
    formatAmount: (value: number) => string;
    formatDate: (iso: string) => string;
    onClose: () => void;
    onConfirm: (params: { installmentId: string; paymentMethod: ManualPaymentMethod }) => void;
}

/**
 * Conferma per registrare a mano l'incasso di una rata.
 *
 * Non è un doppio click di cortesia: l'operazione scrive una riga in `payments`, blocca
 * importo e scadenza della rata, e sulla prima rata fa passare l'itinerario a completato,
 * facendo scadere le proposte alternative.
 *
 * Dal 02/09/2026 la può aprire anche chi vende — TD e agenzia, non più il solo admin — e
 * la contropartita è la presa d'atto qui sotto. I metodi manuali sono per definizione
 * fuori Stripe, quindi la fee Placeefee2Be non viene trattenuta da nessuna parte e resta
 * un debito verso la piattaforma: dirlo dopo, in un riepilogo, sarebbe dirlo a operazione
 * già fatta. La spunta è obbligatoria perché il numero va letto, non scorso.
 */
export const MarkInstallmentPaidModal = ({
    installment,
    isOpen,
    isSaving,
    formatAmount,
    formatDate,
    onClose,
    onConfirm,
}: MarkInstallmentPaidModalProps) => {
    const { t } = useI18n();
    const [method, setMethod] = useState<ManualPaymentMethod>('bank_transfer');
    const [feeAcknowledged, setFeeAcknowledged] = useState(false);

    // Ogni apertura riparte dal metodo più comune e dalla spunta vuota, senza ereditare
    // nulla dalla volta prima: una presa d'atto che sopravvive alla chiusura della modale
    // non è una presa d'atto.
    useEffect(() => {
        if (isOpen) {
            setMethod('bank_transfer');
            setFeeAcknowledged(false);
        }
    }, [isOpen]);

    if (!installment) return null;

    const feeAmount = platformFeeOn(installment.amount);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="sm"
            title={t('checkout.paymentPlan.markPaidModal.title')}
            footer={
                /* Niente wrapper: il footer del Modal e' gia' un flex allineato a destra
                   con il suo gap. Annidarne un altro dava due spaziature diverse fra i
                   bottoni di questa modale e quelli di tutte le altre. */
                <>
                    <Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        type="button"
                        disabled={isSaving || !feeAcknowledged}
                        onClick={() =>
                            onConfirm({ installmentId: installment.id, paymentMethod: method })
                        }
                    >
                        {t('checkout.paymentPlan.markPaidModal.confirm')}
                    </Button>
                </>
            }
        >
            {/* Il Modal non mette padding sul contenuto: sta a ogni modale allinearsi ai
                px-6 py-4 di header e footer, come fa l'editor del piano. Senza, il
                riepilogo della rata toccava i bordi del pannello. */}
            <div className="flex flex-col gap-4 px-6 py-4">
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 flex flex-col gap-1">
                    <span className="font-medium text-gray-900">{installment.label}</span>
                    <div className="flex items-baseline justify-between">
                        <span className="text-sm text-gray-500">
                            {t('checkout.paymentPlan.dueOn', { date: formatDate(installment.due_date) })}
                        </span>
                        <span className="text-lg font-semibold text-gray-900 tabular-nums">
                            {formatAmount(installment.amount)}
                        </span>
                    </div>
                </div>

                <div className="flex flex-col gap-1.5">
                    <Label htmlFor="manual-payment-method">
                        {t('checkout.paymentPlan.markPaidModal.method')}
                    </Label>
                    <Select value={method} onValueChange={(value) => setMethod(value as ManualPaymentMethod)}>
                        <SelectTrigger id="manual-payment-method">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {MANUAL_PAYMENT_METHODS.map((value) => (
                                <SelectItem key={value} value={value}>
                                    {t(`checkout.paymentPlan.markPaidModal.methods.${value}`)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Il blocco della commissione sta sopra quello sulle conseguenze
                    sull'itinerario: è la parte che l'utente non si aspetta, e in fondo
                    alla modale verrebbe letta dopo aver già deciso. */}
                <div className="flex flex-col gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3">
                    <div className="flex items-start gap-2">
                        <Receipt className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div className="flex flex-col gap-1">
                            <span className="text-sm font-semibold text-amber-900">
                                {t('checkout.paymentPlan.markPaidModal.fee.title')}
                            </span>
                            <p className="text-sm text-amber-900">
                                {t('checkout.paymentPlan.markPaidModal.fee.body', {
                                    fee: formatAmount(feeAmount),
                                    rate: `${(PLATFORM_FEE_RATE * 100).toLocaleString('it-IT')}%`,
                                })}
                            </p>
                        </div>
                    </div>

                    <label
                        htmlFor="platform-fee-ack"
                        className="flex items-start gap-2 cursor-pointer text-sm text-amber-900"
                    >
                        <Checkbox
                            id="platform-fee-ack"
                            checked={feeAcknowledged}
                            onCheckedChange={(checked) => setFeeAcknowledged(checked === true)}
                            className="mt-0.5 border-amber-500 data-[state=checked]:bg-amber-600 data-[state=checked]:border-amber-600"
                        />
                        <span>
                            {t('checkout.paymentPlan.markPaidModal.fee.acknowledge', {
                                amount: formatAmount(installment.amount),
                                fee: formatAmount(feeAmount),
                            })}
                        </span>
                    </label>
                </div>

                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-amber-900">
                        {t('checkout.paymentPlan.markPaidModal.warning')}
                    </p>
                </div>
            </div>
        </Modal>
    );
};
